import React from 'react'
import Menu, { MenuProps } from 'components/menu'
import { Delete, Visible, Lock, Group } from 'components/icons'
import { MenuItem, MenuLabel, MenuList, MenuSeparator } from 'components/menu/items'
import { directionOptions, sortLabel } from 'components/spreadsheet/components/menu/views/sort/directions'
import { getViewCapabilities } from 'components/spreadsheet/constants/viewCapabilities'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { getTreeLeafColumnId } from 'components/spreadsheet/helpers/grouping'
import { sortDirections, IFilterType } from 'components/spreadsheet/types'
import { ITableViewColumn } from 'types'

interface BulkColumnMenuProps extends MenuProps {
  // Opens the bulk delete confirmation for the given column IDs (already gated to deletable ones).
  openDeleteColumnsModal: (columnIds: string[]) => void
}

const BulkColumnMenu: React.FC<BulkColumnMenuProps> = ({
  id,
  menuState,
  setMenuState,
  width,
  openDeleteColumnsModal
}) => {
  const { spreadsheetData, setSpreadsheetData, showToolbar, clearColumnSelection, selectedColumnIds } = useDataContext()

  const capabilities = getViewCapabilities(spreadsheetData.viewDetails.type).columnMenu
  const isAdmin = spreadsheetData.isAdmin

  const treeLeafColumnId = getTreeLeafColumnId(
    spreadsheetData.viewDetails.columns,
    spreadsheetData.userConfiguration.groupSettings,
    spreadsheetData.viewDetails.treeGroupView
  )

  const selectedColumns = selectedColumnIds
    .map((columnId) =>
      spreadsheetData.viewDetails.columns.find((column: ITableViewColumn) => column.publicId === columnId)
    )
    .filter((column): column is ITableViewColumn => column !== undefined)

  const sortSettingsByColumnId = new Map(
    spreadsheetData.userConfiguration.sortSettings.map((setting) => [setting.columnId, setting])
  )
  const isGrouped = (columnId: string): boolean =>
    spreadsheetData.userConfiguration.groupSettings.some((setting) => setting.columnId === columnId)
  const isLocked = (columnId: string): boolean =>
    spreadsheetData.userConfiguration.rowLockSettings.some((rule) => rule.columnId === columnId)

  // Per-action eligible subsets — gated exactly as the single-column menu gates each action, so a
  // synced/joined/formula/non-admin column is simply excluded rather than failing mid-run.
  const deletable = selectedColumns.filter((column) => capabilities.deleteColumn && isAdmin && !column.isSynced)
  // A table must keep at least one column, so block the bulk delete when it would remove every column
  // the table has. Compared against the table's columns (not the view's) since deletion is table-wide.
  const wouldEmptyTable = deletable.length > 0 && deletable.length >= spreadsheetData.tableDetails.columns.length
  const hideable = selectedColumns.filter(
    (column) =>
      capabilities.hideColumn && showToolbar && !isGrouped(column.publicId) && treeLeafColumnId !== column.publicId
  )
  const groupable = capabilities.group ? selectedColumns : []
  const sortable = capabilities.sort ? selectedColumns : []
  const lockable = selectedColumns.filter(
    (column) => capabilities.lockColumn && isAdmin && !column.isSynced && !column.formulaEnabled
  )

  // Offer the "undo" variant of a toggle action only when EVERY eligible column already matches the
  // state, mirroring how the single-column menu flips its label (Group ⇄ Ungroup, Lock ⇄ Unlock).
  const allGrouped = groupable.length > 0 && groupable.every((column) => isGrouped(column.publicId))
  const allLocked = lockable.length > 0 && lockable.every((column) => isLocked(column.publicId))
  const allSorted = sortable.length > 0 && sortable.every((column) => sortSettingsByColumnId.has(column.publicId))
  const allSortedAscending =
    sortable.length > 0 &&
    sortable.every((column) => sortSettingsByColumnId.get(column.publicId)?.direction === sortDirections.asc)
  const allSortedDescending =
    sortable.length > 0 &&
    sortable.every((column) => sortSettingsByColumnId.get(column.publicId)?.direction === sortDirections.desc)

  const finish = (): void => {
    clearColumnSelection()
    setMenuState({ ...menuState, open: false })
  }

  const handleHideColumns = (): void => {
    hideable.forEach((column) => setSpreadsheetData({ type: 'HIDE_COLUMN', columnId: column.publicId }))
    finish()
  }

  const handleGroupColumns = (): void => {
    groupable.forEach((column) => {
      if (!isGrouped(column.publicId)) {
        setSpreadsheetData({ type: 'ADD_GROUP', columnId: column.publicId, columnName: column.name })
      }
    })
    finish()
  }

  const handleUngroupColumns = (): void => {
    groupable.forEach((column) => {
      if (isGrouped(column.publicId)) {
        setSpreadsheetData({ type: 'DELETE_GROUP', columnId: column.publicId })
      }
    })
    finish()
  }

  const handleSortColumns = (direction: sortDirections): void => {
    sortable.forEach((column) => {
      if (sortSettingsByColumnId.has(column.publicId)) {
        setSpreadsheetData({ type: 'DELETE_SORT', columnId: column.publicId })
      }
      setSpreadsheetData({ type: 'ADD_SORT', columnId: column.publicId, columnName: column.name })
      if (direction === sortDirections.desc) {
        setSpreadsheetData({
          type: 'CHANGE_SORT_DIRECTION',
          columnId: column.publicId,
          newDirection: sortDirections.desc
        })
      }
    })
    finish()
  }

  const handleRemoveSortColumns = (): void => {
    sortable.forEach((column) => {
      if (sortSettingsByColumnId.has(column.publicId)) {
        setSpreadsheetData({ type: 'DELETE_SORT', columnId: column.publicId })
      }
    })
    finish()
  }

  const handleLockColumns = (): void => {
    const existingLocks = spreadsheetData.userConfiguration.rowLockSettings
    const lockedColumnIds = new Set(existingLocks.map((rule) => rule.columnId))
    const additions = lockable
      .filter((column) => !lockedColumnIds.has(column.publicId))
      .map((column) => ({
        columnId: column.publicId,
        columnName: column.name,
        filterType: IFilterType.all_values,
        value: '',
        multipleValues: [],
        applyToEntireRow: false
      }))
    if (additions.length > 0) {
      setSpreadsheetData({ type: 'UPDATE_LOCKS', locks: [...existingLocks, ...additions] })
    }
    finish()
  }

  const handleUnlockColumns = (): void => {
    const lockableIds = new Set(lockable.map((column) => column.publicId))
    setSpreadsheetData({
      type: 'UPDATE_LOCKS',
      locks: spreadsheetData.userConfiguration.rowLockSettings.filter((rule) => !lockableIds.has(rule.columnId))
    })
    finish()
  }

  const handleDeleteColumns = (): void => {
    openDeleteColumnsModal(deletable.map((column) => column.publicId))
    setMenuState({ ...menuState, open: false })
  }

  // Sorting names its directions when the columns agree on them (all text: A to Z), as the Sort
  // panel does; columns of different kinds get the plain ascending and descending.
  const directionSets = sortable.map((column) => directionOptions(column.kind))
  const directions = directionSets.every((set) => set[0].label === directionSets[0]?.[0].label)
    ? (directionSets[0] ?? directionOptions(undefined))
    : directionOptions(undefined)
  const sortRows = directions
    .filter((direction) => !(direction.value === sortDirections.asc ? allSortedAscending : allSortedDescending))
    .map((direction) => (
      <MenuItem
        key={direction.value}
        icon={direction.icon}
        onClick={() => handleSortColumns(direction.value)}
        data-testid={direction.value === sortDirections.asc ? 'bulk-sort-columns-asc' : 'bulk-sort-columns-desc'}
      >
        {sortLabel(direction.label)}
      </MenuItem>
    ))

  return (
    <Menu id={id} menuState={menuState} setMenuState={setMenuState} width={width}>
      <MenuList>
        <MenuLabel data-testid="bulk-column-count">{selectedColumnIds.length} columns selected</MenuLabel>
        {groupable.length > 0 && (
          <MenuItem
            icon={<Group />}
            onClick={allGrouped ? handleUngroupColumns : handleGroupColumns}
            data-testid="bulk-group-columns"
          >
            {allGrouped ? 'Ungroup columns' : 'Group columns'}
          </MenuItem>
        )}
        {sortable.length > 0 && sortRows}
        {sortable.length > 0 && allSorted && (
          <MenuItem icon={<Delete />} onClick={handleRemoveSortColumns} data-testid="bulk-remove-sort-columns">
            Remove sort
          </MenuItem>
        )}
        {hideable.length > 0 && (
          <MenuItem icon={<Visible />} onClick={handleHideColumns} data-testid="bulk-hide-columns">
            Hide columns
          </MenuItem>
        )}
        {lockable.length > 0 && (
          <MenuItem
            icon={<Lock />}
            onClick={allLocked ? handleUnlockColumns : handleLockColumns}
            data-testid="bulk-lock-columns"
          >
            {allLocked ? 'Unlock columns' : 'Lock columns'}
          </MenuItem>
        )}
        {deletable.length > 0 && (
          <>
            <MenuSeparator />
            <MenuItem
              icon={<Delete />}
              variant="destructive"
              disabledReason={wouldEmptyTable ? 'A table must have at least one column.' : undefined}
              onClick={handleDeleteColumns}
              data-testid="bulk-delete-columns"
            >
              Delete columns
            </MenuItem>
          </>
        )}
      </MenuList>
    </Menu>
  )
}

export default BulkColumnMenu
